import { detectBrowserLanguage, FALLBACK_LANGUAGE } from "../../config.js";

type ColumnFormat = "date" | "datetime" | "time" | "duration" | "status" | "pill" | "text" | undefined;

interface FormatTableValueOptions {
  format?: ColumnFormat;
  valueMap?: Record<string, string>;
  locale: string;
}

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_PATTERN = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const TIME_ONLY_PATTERN = /^([01]?\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function detectTemporalFormat(value: unknown): "date" | "datetime" | "time" | null {
  if (typeof value === "string") {
    if (ISO_DATE_PATTERN.test(value)) {
      return "date";
    }
    if (ISO_DATETIME_PATTERN.test(value)) {
      return "datetime";
    }
    if (TIME_ONLY_PATTERN.test(value)) {
      return "time";
    }
  }

  if (value instanceof Date) {
    const hasTime = value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0;
    return hasTime ? "datetime" : "date";
  }

  return null;
}

function formatTimeOnly(value: string, locale: string): string {
  const [rawHours, rawMinutes, rawSeconds] = value.split(":");
  const hours = Number(rawHours) || 0;
  const minutes = Number(rawMinutes) || 0;
  const seconds = rawSeconds !== undefined ? Number(rawSeconds) || 0 : undefined;
  const date = new Date();
  date.setHours(hours, minutes, seconds ?? 0, 0);
  const options: Intl.DateTimeFormatOptions = seconds !== undefined
    ? { hour: "2-digit", minute: "2-digit", second: "2-digit" }
    : { hour: "2-digit", minute: "2-digit" };
  return new Intl.DateTimeFormat(locale, options).format(date);
}

function formatDuration(value: number): string {
  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const chunks: string[] = [];

  if (hours > 0) {
    chunks.push(`${hours}h`);
  }
  if (minutes > 0) {
    chunks.push(`${minutes}m`);
  }
  if (seconds > 0 && hours === 0) {
    chunks.push(`${seconds}s`);
  }

  return chunks.length > 0 ? chunks.join(" ") : `${totalSeconds}s`;
}

export function getUserLocale(preferred?: string): string {
  if (preferred && preferred.trim().length > 0) {
    return preferred.trim();
  }

  return detectBrowserLanguage() || FALLBACK_LANGUAGE;
}

export function formatTableValue(value: unknown, options: FormatTableValueOptions): string {
  const { format, valueMap, locale } = options;

  if (valueMap && value !== undefined && value !== null) {
    const key = String(value);
    if (Object.prototype.hasOwnProperty.call(valueMap, key)) {
      const mapped = valueMap[key];
      return mapped ?? "";
    }
  }

  if (value === undefined || value === null) {
    return "–";
  }

  if (typeof value === "boolean") {
    return value ? "Ja" : "Nein";
  }

  const effectiveFormat = format ?? detectTemporalFormat(value);
  if (effectiveFormat === "date" || effectiveFormat === "datetime") {
    const parsedDate = parseDateValue(value);
    if (parsedDate) {
      const formatter = new Intl.DateTimeFormat(locale, effectiveFormat === "date"
        ? { dateStyle: "short" }
        : { dateStyle: "short", timeStyle: "short" });
      return formatter.format(parsedDate);
    }
  }

  if (effectiveFormat === "time") {
    if (typeof value === "string" && TIME_ONLY_PATTERN.test(value.trim())) {
      return formatTimeOnly(value.trim(), locale);
    }

    const parsedTime = parseDateValue(value);
    if (parsedTime) {
      return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(parsedTime);
    }
  }

  if (effectiveFormat === "duration") {
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isNaN(numericValue)) {
      return formatDuration(numericValue);
    }
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString(locale) : String(value);
  }

  if (value instanceof Date) {
    const formatter = new Intl.DateTimeFormat(locale, { dateStyle: "short", timeStyle: "short" });
    return formatter.format(value);
  }

  return String(value);
}
